import { Cell } from "@tanstack/react-table";
import { isValidElement } from "react";

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function toAbsoluteUrl(url: string): string {
  return url.startsWith("http") ? url : `${window.location.origin}${url}`;
}

/**
 * Recursively walks a React element tree looking for a link URL.
 * Detects `to` (react-router Link / EntityLink) and `href` (plain <a>).
 */
function extractLinkUrl(element: unknown): string | null {
  if (!isValidElement(element)) return null;
  const props = element.props as Record<string, unknown>;

  if (typeof props.to === "string") return props.to;
  if (typeof props.href === "string") return props.href;

  const children = props.children;
  if (isValidElement(children)) {
    return extractLinkUrl(children);
  }
  if (Array.isArray(children)) {
    for (const child of children) {
      const url = extractLinkUrl(child);
      if (url) return url;
    }
  }
  return null;
}

/**
 * Extracts text and HTML from a TanStack Table cell by calling its render
 * function in memory (works with virtualized tables — no DOM needed).
 *
 * When the cell renderer returns a React element containing a link
 * (react-router `to` or plain `href`), the HTML output wraps the text
 * in an `<a>` tag with an absolute URL.
 *
 * @param fallbackUrl - optional URL to use when the renderer itself
 *   doesn't produce a link (e.g. pathBuilder on the first visible column).
 */
export function getCellHtmlValue<TData>(
  cell: Cell<TData, unknown>,
  fallbackUrl?: string | null,
): { text: string; html: string } {
  const renderFn = cell.column.columnDef.cell;

  let text: string;
  let rendererUrl: string | null = null;

  if (typeof renderFn === "function") {
    try {
      const result = renderFn(cell.getContext());

      if (typeof result === "string") {
        text = result;
      } else if (result == null) {
        text = "";
      } else if (isValidElement(result)) {
        text = String(cell.getValue() ?? "");
        rendererUrl = extractLinkUrl(result);
      } else {
        text = String(result);
      }
    } catch {
      text = String(cell.getValue() ?? "");
    }
  } else {
    text = String(cell.getValue() ?? "");
  }

  const escaped = escapeHtml(text);
  const rawUrl = rendererUrl ?? fallbackUrl ?? null;
  const html = rawUrl
    ? `<a href="${escapeHtml(toAbsoluteUrl(rawUrl))}">${escaped}</a>`
    : escaped;

  return { text, html };
}
